import { useEffect, useMemo, useState } from 'react';
import { news, newsCategories } from '@/mocks/news';
import type { NewsItem } from '@/types';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 6;

export default function News() {
  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);
  const [active, setActive] = useState<NewsItem | null>(null);

  usePageMeta(
    '资讯专题 - 韩剧剧情分析与演员动态 - 韩剧',
    '韩剧资讯专题，提供最新韩剧剧情分析、演员动态、预告速递与行业资讯，追剧之余了解台前幕后的故事。',
  );

  useEffect(() => {
    document.body.style.overflow = active ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [active]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const filtered = useMemo(
    () => (category === '全部' ? news : news.filter((n) => n.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const featured = news[0];

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">资讯专题</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            剧情分析、演员动态、预告速递与行业观察，带你看见韩剧台前幕后的精彩故事。
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <button
          onClick={() => setActive(featured)}
          className="group w-full text-left cursor-pointer rounded-lg overflow-hidden border border-background-200 bg-background-50 mb-8 md:mb-10 block"
        >
          <div className="flex flex-col md:flex-row">
            <div className="w-full md:w-1/2 aspect-[16/10] md:aspect-auto md:min-h-[280px] overflow-hidden bg-background-200">
              <img src={featured.cover} alt={featured.title} title={`${featured.title} - 韩剧资讯`} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-medium text-white bg-primary-500 rounded-full px-3 py-1">头条</span>
                <span className="text-xs text-accent-700 bg-accent-100 rounded-full px-2.5 py-1">{featured.category}</span>
                <span className="text-xs text-foreground-400">{featured.date}</span>
              </div>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                {featured.title}
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-3">{featured.summary}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                阅读全文 <i className="ri-arrow-right-line"></i>
              </span>
            </div>
          </div>
        </button>

        <div className="flex flex-wrap items-center gap-1 bg-background-100 border border-background-200 rounded-full p-1 mb-6 w-fit">
          {newsCategories.map((c) => (
            <button
              key={c}
              onClick={() => { setCategory(c); setPage(1); }}
              className={`cursor-pointer whitespace-nowrap text-sm px-4 py-1.5 rounded-full transition-colors ${
                category === c ? 'bg-primary-500 text-white font-medium' : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <ul className="divide-y divide-background-200 rounded-lg border border-background-200 overflow-hidden">
          {pageItems.map((n) => (
            <li key={n.id}>
              <button
                onClick={() => setActive(n)}
                className="w-full text-left flex gap-4 p-4 md:p-5 hover:bg-background-100 transition-colors cursor-pointer group"
              >
                <div className="hidden sm:block w-40 shrink-0 aspect-[16/10] rounded-md overflow-hidden bg-background-200">
                  <img src={n.cover} alt={n.title} title={`${n.title} - 韩剧资讯`} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-medium text-accent-700 bg-accent-100 rounded-full px-2 py-0.5">{n.category}</span>
                    <span className="text-xs text-foreground-400">{n.date}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                    {n.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 leading-relaxed line-clamp-2">{n.summary}</p>
                </div>
              </button>
            </li>
          ))}
        </ul>

        {totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={current === 1}
              className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`cursor-pointer w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-colors ${
                  n === current ? 'bg-primary-500 text-white' : 'border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={current === totalPages}
              className="cursor-pointer w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-primary-400 hover:text-primary-600"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      {active && (
        <div className="fixed inset-0 z-[60] flex items-start md:items-center justify-center p-0 md:p-6" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/60" onClick={() => setActive(null)}></div>
          <div className="relative w-full md:max-w-2xl bg-background-50 rounded-none md:rounded-lg overflow-hidden max-h-screen md:max-h-[85vh] flex flex-col animate-fade-up">
            <div className="relative h-48 md:h-64 shrink-0 bg-background-200">
              <img src={active.cover} alt={active.title} title={`${active.title} - 韩剧资讯`} className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <button
                onClick={() => setActive(null)}
                aria-label="关闭"
                className="cursor-pointer absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
              <div className="absolute bottom-4 left-5 right-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-white bg-accent-500 rounded-full px-2.5 py-1">{active.category}</span>
                  <span className="text-xs text-white/80">{active.date}</span>
                </div>
              </div>
            </div>
            <div className="p-5 md:p-7 overflow-y-auto">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 leading-snug">{active.title}</h2>
              <div className="mt-4 space-y-3">
                {active.content.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed">{p}</p>
                ))}
              </div>
              <div className="mt-6 pt-5 border-t border-background-200 flex items-center justify-between">
                <span className="text-xs text-foreground-400">内容为演示资讯，仅供浏览</span>
                <button onClick={() => setActive(null)} className="cursor-pointer whitespace-nowrap text-sm text-primary-600 hover:text-primary-700 font-medium">
                  关闭阅读
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}